import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import classnames from 'classnames'
import queryString from 'query-string'

import { DigitalSurface } from 'src/apollo/onlineOrdering'
import { toBase64 } from 'src/lib/js/base64'
import { useQueryString } from 'src/lib/js/hooks/useQueryString'
import { TOAST_LOCAL_SEARCH_PATH } from 'src/public/js/siteUtilities'
import { LocationSearchError } from 'src/shared/components/common/location_search/LocationSearchError'
import { LocationSearchErrorContextProvider } from 'src/shared/components/common/location_search/LocationSearchErrorContext'

import PlacesAutocomplete from 'shared/components/common/location_search/PlacesAutocomplete'

import { fetchUserLocation } from 'public/components/toast_local/rx_search/locationUtils'

import { gMapsAPIKey } from 'config'

import { t } from 'src/translations'

export type Props = {
  placeholder?: string
  className?: string
  city?: City
  cuisineId?: number
  resetString?: boolean
  onStringReset?: () => void
  searchMode?:
    | 'text' // "location" mode uses google places autocomplete to search by location.
    | 'location' // "text" mode searches for food and cuisine types with plaintext
  searchPath?: string
}

export type City = {
  parentCity?: City
  city: string
  state: string
  lat: string
  long: string
}

type Filters = { [filter: string]: string | number }

const SearchBar = ({
  placeholder = t('search-bar.restaurant-or-cuisine'),
  className,
  city,
  cuisineId,
  resetString,
  onStringReset,
  searchMode = 'text',
  searchPath = TOAST_LOCAL_SEARCH_PATH
}: Props) => {
  const parsedQS = useQueryString(['filters'], ['filters'])
  const history = useHistory()

  const [searchString, setSearchString] = useState(
    parsedQS.filters ? parsedQS.filters.search : ''
  )

  const search = useCallback(
    (filters: Filters) => {
      let searchValues = ''
      if (Object.keys(filters).length) {
        searchValues = `?${queryString.stringify({
          filters: toBase64(JSON.stringify(filters))
        })}`
      }
      const url = searchPath + searchValues
      history.push(url)
    },
    [history, searchPath]
  )

  const newFilters = useMemo(() => {
    const filters: Filters = {}
    if (city) {
      if (city.parentCity) {
        filters['userCity'] = city.parentCity.city
        filters['userLat'] = city.parentCity.lat
        filters['userLong'] = city.parentCity.long
      } else {
        filters['userCity'] = city.city
        filters['userLat'] = city.lat
        filters['userLong'] = city.long
      }
      filters['userState'] = city.state
    }
    if (cuisineId) {
      filters['cuisineId'] = cuisineId
    }
    if (searchString) {
      filters['search'] = searchString
    }

    return filters
  }, [searchString, city, cuisineId])

  const buildPlaceFilters = (
    place: Omit<google.maps.places.PlaceResult, 'name'>
  ) => {
    // prepare place filters;
    let street: string | undefined = undefined
    let neighborhood: string | undefined = undefined
    let city: string | undefined = undefined
    let state: string | undefined = undefined
    let lat: number | undefined = undefined
    let long: number | undefined = undefined
    const selectedPlaceFilters: Filters = {}
    if (place && place.geometry?.location && place.address_components) {
      lat = place.geometry.location.lat()
      long = place.geometry.location.lng()
      if (lat && long) {
        selectedPlaceFilters['userLat'] = lat
        selectedPlaceFilters['userLong'] = long
      }
      for (const component of place.address_components) {
        for (const componentType of component.types) {
          if (componentType === 'street_number') {
            street = street
              ? component.long_name + ' ' + street
              : component.long_name
            selectedPlaceFilters['userStreet'] = street
            break
          } else if (componentType === 'route') {
            street = street
              ? street + ' ' + component.short_name
              : component.short_name
            selectedPlaceFilters['userStreet'] = street
            break
          } else if (componentType === 'neighborhood') {
            neighborhood = component.long_name
            selectedPlaceFilters['userNeighborhood'] = neighborhood || ''
            break
          } else if (componentType === 'locality') {
            city = component.long_name
            selectedPlaceFilters['userCity'] = city
            break
          } else if (componentType === 'administrative_area_level_1') {
            state = component.short_name
            selectedPlaceFilters['userState'] = state
            break
          }
        }
      }
    }
    return selectedPlaceFilters
  }

  const launchTextSearch = useCallback(() => {
    const filters = { ...parsedQS.filters, ...newFilters }
    if (filters.userCity || (filters.userLat && filters.userLong)) {
      search(filters)
    } else {
      // searching without a place defaults to using the user's location.
      fetchUserLocation((userLocation) => {
        search({ ...parsedQS.filters, ...userLocation, ...newFilters })
      })
    }
  }, [parsedQS.filters, newFilters, search])

  const launchPlaceSearch = useCallback(
    (place: Omit<google.maps.places.PlaceResult, 'name'>) => {
      const selectedPlaceFilters = buildPlaceFilters(place)
      search({ ...parsedQS.filters, ...newFilters, ...selectedPlaceFilters })
    },
    [newFilters, parsedQS, search]
  )

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchString(event.target.value)
  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      launchTextSearch()
    }
  }

  useEffect(() => {
    if (resetString) {
      setSearchString('')
      if (onStringReset) {
        onStringReset()
      }
    }
  }, [resetString, onStringReset])
  return (
    <LocationSearchErrorContextProvider>
      <div className='searchBarContainer'>
        <div className={classnames('search-bar', className)}>
          {searchMode === 'text' ? (
            <input
              placeholder={placeholder}
              value={searchString}
              onChange={onSearchChange}
              onKeyDown={onSearchKeyDown}
              aria-label={t('search-bar.search')}
            />
          ) : (
            <PlacesAutocomplete
              id='tl-splash-search-bar'
              placeholder={t('search-bar.enter-city')}
              showGenericSpinner
              apiKey={gMapsAPIKey}
              useCurrentLocation
              autoPromptCurrentLocation={false}
              onPlaceSelected={(place) => launchPlaceSearch(place)}
              source={DigitalSurface.Local}
            />
          )}
        </div>
        <LocationSearchError />
      </div>
    </LocationSearchErrorContextProvider>
  )
}

export default SearchBar
